import { APP_STORAGE_PREFIX } from '../../config/config.js'

// The post-update notice (shared/components/AnnouncementModal.jsx) and logout (hooks/useAuth.jsx)
// must name the SAME key: logout clears it so the notice shows again at the next sign-in. They each
// spelled it out by hand before, so bumping the version in one place would have left logout
// clearing a key nobody reads.
//
// Bump the suffix whenever the notice's message changes, so everyone sees the new one.
//   _v1  the generic "the app has recently been updated" message (production 2.61.1)
//   _v2  update 1 (2.98.0): what changed, by role
//   _v3  update 2 (2.99.40): what changed, by role
//
// EDITING UPDATE 2's COPY BEFORE UPDATE 2 SHIPS DOES NOT NEED A BUMP. The rule above is for a
// message people have already READ: bumping tells them there is a new one. Production is still on
// 2.98.x and has only ever seen `_v2`, so every edit to `_v3`'s words up to update-2 day reaches
// everybody as one new notice. 2.99.54 added four lines under that rule and deliberately left the
// suffix alone. After update 2 ships, a change to these words does need `_v4`.
export const ANNOUNCEMENT_ACK_KEY = `${APP_STORAGE_PREFIX}_announcement_ack_v3`

// Keys from earlier notices. Logout removes these too, so nothing is left behind in the browser.
export const RETIRED_ANNOUNCEMENT_ACK_KEYS = [
  `${APP_STORAGE_PREFIX}_announcement_ack_v1`,
  `${APP_STORAGE_PREFIX}_announcement_ack_v2`,
]

// Update 2 (2.99.40): which parts of the notice each role sees. See AnnouncementModal.jsx.
//
// The undo window: until then 2.98.x can still be put back, as long as nobody saves something it
// would misread (decision 4a: no Lock & Save, no correcting a locked week, no second release or
// claim on a job; unit-price claims start after it, decision 24a). After this moment the rules are
// no longer shown. IF UPDATE 2 MOVES, move this to the Friday 17:00 Central after the new date and
// change the text below to match - a test fails if the two name different days.
//
// It moved once: planned for Sun 18 Oct (window to Fri 23 Oct), brought forward to Sun 27 Sep by the
// owner on 19 Sep ("ship update 2 asap"). This still said 23 October until 21 Sep, which would have
// told every member of staff the rules lasted three weeks longer than they do.
export const UNDO_WINDOW_ENDS_AT = Date.parse('2026-10-02T17:00:00-05:00')
export const UNDO_WINDOW_ENDS_TEXT = 'Friday 2 October, 5 pm'

const ADMIN_ROLES = ['system_admin', 'master_admin', 'admin']
const OFFICE_ROLES = [...ADMIN_ROLES, 'accounting', 'office_staff']
// Lock & Save needs `tk:view_all`, which foremen hold as well as the office, and approving a day is
// what re-splits later tickets. Managers are in because the billing half reaches them too.
const PAYROLL_ROLES = [...OFFICE_ROLES, 'foreman', 'superintendent', 'manager']
const BILLING_ROLES = [...OFFICE_ROLES, 'manager']
// Outside the company: they get one line only.
const EXTERNAL_ROLES = ['client', 'subcontractor', 'vendor']

// Which sections a role sees. A custom or unknown role sees the staff sections only.
//
// THE UNDO-WINDOW RULES GO TO EVERY STAFF ROLE, not only the office as in update 1. Update 1's
// rules were about screens only the office opens; update 2's first rule is a button foremen can
// press, and a company's custom role can hold that right too. A rule nobody was shown is a rule
// that makes the way back unsafe, and reading one line that does not apply costs nothing.
export function noticeSectionsFor(role, now = Date.now()) {
  if (EXTERNAL_ROLES.includes(role)) return ['external']
  const sections = ['everyone']
  if (now < UNDO_WINDOW_ENDS_AT) sections.push('undoWindow')
  if (PAYROLL_ROLES.includes(role)) sections.push('payroll')
  if (BILLING_ROLES.includes(role)) sections.push('billing')
  return sections
}

// What an acknowledgement is FOR: this person, in this sign-in. The stamp is set at sign-in
// (sessionPolicy.startSession) and removed by every sign-out path - logout, the idle limit, the total
// limit, a refused refresh - so a new sign-in, or a different person on the same browser, sees the
// notice again without each of those paths having to remember this key.
//
// KEPT, BUT NO LONGER WHAT DECIDES WHETHER THE NOTICE APPEARS. The notice is not forced open any
// more (owner, 2026-09-16, walking staging: "lets not have it as a popup immediately"); it opens
// from the What changed button in the header. See ANNOUNCEMENT_SEEN_KEY below.
export function acknowledgementMarkFor(user, sessionStartedAt) {
  const who = user?.id ?? user?.user_id ?? user?.email ?? ''
  return `${who}|${sessionStartedAt}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Has this person ever OPENED the notice? Drives the unread dot on the button, and nothing else.
//
// A SEPARATE KEY FROM THE ACKNOWLEDGEMENT, and deliberately not cleared by logout. The old key is
// per sign-in on purpose, so a forced notice came back every time somebody signed in. A dot that
// behaved that way would be the popup again in a smaller shape - nagging every morning about an
// update the person read once in October. This one is per PERSON and it stays read.
// Bumped with each update's notice (`_v3` = update 2), so everybody gets the dot once more.
export const ANNOUNCEMENT_SEEN_KEY = `${APP_STORAGE_PREFIX}_announcement_seen_v3`

export function seenMarkFor(user) {
  return String(user?.id ?? user?.user_id ?? user?.email ?? '')
}
